// Traffic generator: realistic flight plans for an airport and a session (airlines with their ICAO codes and
// radiotelephony designators, fleet, destinations, requested levels by distance and the semicircular rule,
// SID exit / STAR entry by direction, stands by wing span).
import type { AirportMeta, AirlineMeta } from '../data/types';
import type { Airport } from './airport';
import type { SpawnSpec } from './world';
import type { FlightPlan } from './flightplan';
import { correctFL } from './flightplan';
import { Rng } from './rng';
import { DESTINATIONS } from '../data/destinations';
import { typeOf } from './perf';
import { Projection, bearing, angDiff, norm360, NM } from './geo';

export interface TrafficSpec {
  minutes: number;
  departures: number;
  arrivals: number;
  /** Arrivals already established on final at the start (distances in NM). */
  onFinal?: number[];
  /** Share of flight plans with an error for the delivery controller. */
  fpErrorRate?: number;
  /** Include the Airbus Beluga / heavy traffic. */
  heavies?: boolean;
  /** Spread: 'even' or a wave peaking in the middle of the session. */
  pattern?: 'even' | 'wave';
}

export function generateTraffic(meta: AirportMeta, ap: Airport, spec: TrafficSpec, seed: number): SpawnSpec[] {
  const rng = new Rng(seed ^ 0x51ab);
  const out: SpawnSpec[] = [];
  const used = new Set<string>();
  const usedStands = new Set<string>();
  const proj = new Projection(meta.arp);
  const exits = [...new Set(meta.sids.map(s => s.exit))];
  const entries = [...new Set(meta.stars.map(s => s.entry))];
  const fixBrg = (name: string) => { const f = meta.fixes[name]; if (!f) return 0; const p = proj.toXY(f.lat, f.lon); return bearing(0, 0, p.x, p.y); };
  const destBrg = (icao: string) => { const d = DESTINATIONS[icao]; if (!d) return 0; const p = proj.toXY(d.lat, d.lon); return bearing(0, 0, p.x, p.y); };
  const destNm = (icao: string) => { const d = DESTINATIONS[icao]; if (!d) return 300; const p = proj.toXY(d.lat, d.lon); return Math.hypot(p.x, p.y) / NM; };
  const nearest = (list: string[], brg: number) => list.reduce((best, f) => (Math.abs(angDiff(fixBrg(f), brg)) < Math.abs(angDiff(fixBrg(best), brg)) ? f : best), list[0]!);

  const airlines = meta.traffic.airlines.filter(a => spec.heavies !== false || !a.fleet.every(t => typeOf(t).wake === 'H'));
  const callsign = (al: AirlineMeta): { cs: string; flightId: string } => {
    for (let i = 0; i < 80; i++) {
      let flightId: string;
      if (al.num === 'reg') flightId = 'E' + pick3(rng);
      else if (al.num === 'short') flightId = String(rng.int(1, 9)) + String(rng.int(0, 99));
      else if (al.num === 'num') flightId = String(rng.int(1, 9)) + String(rng.int(0, 999)).padStart(rng.int(1, 3), '0');
      else flightId = rng.chance(0.5) ? String(rng.int(1, 2999)) : String(rng.int(1, 99)) + letter(rng) + (rng.chance(0.6) ? letter(rng) : '');
      const cs = al.num === 'reg' ? `D${flightId}` : al.icao + flightId;
      if (!used.has(cs)) { used.add(cs); return { cs, flightId: al.num === 'reg' ? `D${flightId}` : flightId }; }
    }
    throw new Error('no free callsign');
  };
  const standFor = (span: number, ga: boolean): string | undefined => {
    const all = Object.values(ap.stands).filter(s => s.maxSpan >= span && !usedStands.has(s.id) && (ga ? s.ga || s.maxSpan <= 36 : !s.ga));
    if (!all.length) return undefined;
    const s = rng.pick(all);
    usedStands.add(s.id);
    return s.id;
  };
  const times = (n: number, from: number, to: number) => {
    const res: number[] = [];
    for (let i = 0; i < n; i++) {
      const u = (i + rng.range(0.15, 0.85)) / n;
      const shaped = spec.pattern === 'wave' ? 0.5 + (u - 0.5) * (0.55 + 0.45 * Math.abs(u - 0.5) * 2) : u;
      res.push(from + shaped * (to - from));
    }
    return res;
  };

  const flight = (dep: boolean): FlightPlan => {
    const al = rng.weighted(airlines, a => a.weight);
    const { cs, flightId } = callsign(al);
    const type = rng.pick(al.fleet);
    const other = rng.pick(al.dest);
    const brg = destBrg(other);
    const magTrack = norm360(brg - meta.magVar);
    const t = typeOf(type);
    const nm = destNm(other);
    let rfl = nm < 120 ? rng.int(10, 16) * 10 : nm < 280 ? rng.int(20, 28) * 10 : rng.int(32, Math.min(39, t.ceilingFL / 10)) * 10;
    rfl = Math.min(rfl, t.typicalFL + 20);
    rfl = correctFL(rfl, dep ? magTrack : norm360(magTrack + 180));
    const fp: FlightPlan = {
      callsign: cs, telephony: al.num === 'reg' ? '' : al.telephony, flightId, airline: al.icao, type,
      rules: al.num === 'reg' ? 'V' : 'I', dep: dep ? meta.icao : other, dest: dep ? other : meta.icao,
      route: '', rfl,
    };
    if (dep) {
      fp.exitFix = nearest(exits, brg);
      fp.route = `${fp.exitFix} DCT ${other}`;
      if (spec.fpErrorRate && rng.chance(spec.fpErrorRate)) { fp.rfl += 10; fp.error = 'RFL_PARITY'; }
    } else {
      fp.entryFix = nearest(entries, brg);
      fp.route = `${other} DCT ${fp.entryFix}`;
    }
    fp.stand = standFor(t.spanM, al.num === 'reg');
    return fp;
  };

  const total = spec.minutes * 60;
  for (const t of times(spec.departures, 0, total * 0.85)) {
    const fp = flight(true);
    if (!fp.stand) continue;
    fp.eobt = t + 300;
    out.push({ t, kind: 'DEP', fp });
  }
  // arrivals appear about 12 minutes before landing at the sector boundary
  for (const t of times(spec.arrivals, 0, Math.max(60, total - 14 * 60))) {
    const fp = flight(false);
    out.push({ t, kind: 'ARR', fp, startNm: rng.range(2, 8) });
  }
  for (const d of spec.onFinal ?? []) {
    const fp = flight(false);
    out.push({ t: 0, kind: 'ARR', fp, onFinalNm: d });
  }
  return out.sort((a, b) => a.t - b.t);
}

const L = 'ABCDEFGHJKLMNPRSTUVWXYZ';
function letter(r: Rng): string { return L[r.int(0, L.length - 1)]!; }
function pick3(r: Rng): string { return letter(r) + letter(r) + letter(r); }
